import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Checklist, ChecklistListApiResponse, ChecklistItemDto, ChecklistDetailApiResponse, ChecklistDetailDto, CreateChecklistRequest, CreateChecklistResponse } from '../models/Checklist';
import { ContractsService } from './contracts.service';
import { VehiclesService } from './vehicles.service';

@Injectable({ providedIn: 'root' })
export class ChecklistService {
  /**
   * Base da API de checklist preventiva.
   *
   * Em desenvolvimento, usa o proxy `/api` (environment.microservices.operation),
   * e em produção aponta para a Azure Function de operações:
   * https://erp-dev-operation-hkb2crf5e7e5d5c0.brazilsouth-01.azurewebsites.net/api/checklist
   */
  private readonly baseUrl = `${environment.microservices.operation}/checklist`;

  constructor(
    private http: HttpClient,
    private contractsService: ContractsService,
    private vehiclesService: VehiclesService
  ) {}

  /**
   * Lista checklists usando o novo endpoint com paginação e filtros
   * @param page Número da página (padrão: 1)
   * @param pageSize Tamanho da página (padrão: 10)
   * @param status Filtro por status (opcional: Draft, InProgress, Completed, Cancelled)
   * @returns Resposta completa da API com sucesso, mensagem, dados e contagem
   */
  getAll(page: number = 1, pageSize: number = 10, status?: string): Observable<ChecklistListApiResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());
    
    if (status) {
      params = params.set('status', status);
    }
    
    return this.http.get<ChecklistListApiResponse>(`${this.baseUrl}/list`, { params });
  }

  /**
   * Lista checklists por placa do veículo (lazy load na OS).
   * Endpoint: GET /checklist/list?page=1&pageSize=10&plateNumber=ABC1234
   */
  getAllByPlateNumber(
    plateNumber: string,
    page: number = 1,
    pageSize: number = 10,
    status?: string
  ): Observable<ChecklistListApiResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString())
      .set('plateNumber', (plateNumber || '').toString().trim());

    if (status) {
      params = params.set('status', status);
    }

    return this.http.get<ChecklistListApiResponse>(`${this.baseUrl}/list`, { params });
  }

  /**
   * Lista todos os checklists e retorna apenas o array de dados
   * Útil para compatibilidade com código existente
   */
  getAllAsArray(): Observable<ChecklistItemDto[]> {
    return this.getAll().pipe(
      map(response => response.data)
    );
  }

  /**
   * Busca um checklist por ID com detalhes completos (Sections e Items)
   * @param id ID do checklist
   * @param includeDetails Se true, inclui Sections e Items (padrão: true)
   */
  getById(id: number, includeDetails: boolean = true): Observable<ChecklistDetailApiResponse> {
    // Sempre incluir includeDetails na URL como query parameter
    const url = `${this.baseUrl}/${id}?includeDetails=${includeDetails}`;
    return this.http.get<ChecklistDetailApiResponse>(url);
  }

  /**
   * Busca um checklist por ID e retorna apenas os dados (compatibilidade)
   * @param id ID do checklist
   */
  getByIdAsData(id: number): Observable<ChecklistDetailDto> {
    return this.getById(id).pipe(
      map(response => response.data)
    );
  }

  /**
   * Cria um novo checklist baseado no template
   * @param payload Dados do checklist a ser criado
   */
  create(payload: CreateChecklistRequest): Observable<CreateChecklistResponse> {
    return this.http.post<CreateChecklistResponse>(`${this.baseUrl}`, payload);
  }

  /**
   * Cria um novo checklist completo com todos os itens de uma vez
   * @param payload Dados do checklist com todos os itens incluídos
   */
  createFull(payload: CreateChecklistRequest): Observable<CreateChecklistResponse> {
    return this.http.post<CreateChecklistResponse>(`${this.baseUrl}/full`, payload);
  }

  /**
   * Cria um checklist B2C completo vinculado ao contrato (template full).
   * Usado quando o contrato ainda não tem checklist – após criar, o usuário é
   * direcionado ao formulário de checklist para preencher (mesma rotina do checklist-form).
   * @param contractDraftId ID do contrato (B2C)
   * @returns Resposta com id do checklist criado (CreateChecklistResponse)
   */
  createForContract(contractDraftId: number): Observable<CreateChecklistResponse> {
    return this.http.post<CreateChecklistResponse>(
      `${this.baseUrl}/contract/${contractDraftId}/b2c/full`,
      {}
    );
  }

  /**
   * Cria um checklist B2C completo vinculado ao contrato com todos os itens preenchidos.
   * Chamado ao salvar (última etapa) quando o checklist foi aberto pelo contrato (fromContract).
   * Endpoint: POST /checklist/contract/{contractDraftId}/b2c/full
   * @param contractDraftId ID do contrato (B2C)
   * @param payload Dados completos do checklist (mesmo formato do createFull)
   * @returns Resposta com id do checklist criado (CreateChecklistResponse)
   */
  createFullForContract(contractDraftId: number, payload: CreateChecklistRequest): Observable<CreateChecklistResponse> {
    return this.http.post<CreateChecklistResponse>(
      `${this.baseUrl}/contract/${contractDraftId}/b2c/full`,
      payload
    );
  }

  /**
   * Método de compatibilidade (mantido para não quebrar código existente)
   * @deprecated Use create() com CreateChecklistRequest
   */
  createLegacy(payload: Checklist): Observable<Checklist> {
    return this.http.post<Checklist>(`${this.baseUrl}`, payload);
  }

  update(id: number, payload: Checklist): Observable<Checklist> {
    return this.http.put<Checklist>(`${this.baseUrl}/${id}`, payload);
  }

  /**
   * Cancela o checklist (POST /checklist/{id}/cancel).
   * Falha se o corpo trouxer `error`, `success: false` ou não indicar sucesso.
   */
  cancelChecklist(id: number): Observable<ChecklistDetailApiResponse> {
    return this.http
      .post<ChecklistDetailApiResponse | Record<string, unknown>>(`${this.baseUrl}/${id}/cancel`, {})
      .pipe(
        map((response) => this.assertChecklistMutationSuccess(response)),
        catchError((err) => throwError(() => this.normalizeChecklistHttpError(err)))
      );
  }

  /** Resposta de sucesso da API de checklist (evita tratar `{ error: "..." }` como OK). */
  private assertChecklistMutationSuccess(response: unknown): ChecklistDetailApiResponse {
    const body = response as Record<string, unknown> | null | undefined;
    if (!body || typeof body !== 'object') {
      throw { error: { error: 'Resposta inválida do servidor.' } };
    }

    const apiError = this.extractChecklistApiErrorMessage(body);
    if (apiError) {
      throw { error: body };
    }

    if (body['success'] === true || body['data']) {
      return body as unknown as ChecklistDetailApiResponse;
    }

    throw { error: body };
  }

  private extractChecklistApiErrorMessage(body: Record<string, unknown>): string | null {
    if (body['success'] === false) {
      const msg = body['message'];
      if (typeof msg === 'string' && msg.trim()) {
        return msg.trim();
      }
    }
    const err = body['error'] ?? body['Error'];
    if (typeof err === 'string' && err.trim()) {
      return err.trim();
    }
    return null;
  }

  private normalizeChecklistHttpError(err: unknown): { error: unknown } {
    if (err && typeof err === 'object' && 'error' in (err as object)) {
      return err as { error: unknown };
    }
    return { error: err };
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  uploadPhoto(id: number, file: File, meta?: Record<string, string>): Observable<{ url: string }> {
    const form = new FormData();
    form.append('file', file);
    if (meta) {
      Object.entries(meta).forEach(([k, v]) => form.append(k, v));
    }
    return this.http.post<{ url: string }>(`${this.baseUrl}/${id}/photos`, form);
  }

  /**
   * Faz upload do PDF do checklist
   * @param checklistId ID do checklist
   * @param pdfBlob Blob do PDF gerado
   */
  uploadPdf(checklistId: number, pdfBlob: Blob): Observable<{ url: string; success: boolean }> {
    const form = new FormData();
    const pdfFile = new File([pdfBlob], `checklist-${checklistId}.pdf`, { type: 'application/pdf' });
    form.append('file', pdfFile);
    form.append('type', 'pdf');
    form.append('fileType', 'application/pdf'); // Garantir que o tipo seja PDF
    form.append('preserveFormat', 'true'); // Flag para não converter para PNG
    form.append('checklistId', checklistId.toString());
    form.append('isPdf', 'true'); // Flag adicional para identificar como PDF
    
    
    return this.http.post<{ url: string; success: boolean }>(`${this.baseUrl}/${checklistId}/pdf`, form, {
      headers: {
        // Não definir Content-Type aqui - o browser define automaticamente com boundary para FormData
      }
    });
  }

  /**
   * Busca veículo por placa
   * PRIMEIRO busca contratos pela placa usando: /erp-dev-contract/search/contracts?q=PLACA
   * SE NÃO encontrar contrato, AÍ busca na tabela de veículos
   * Retorna dados do veículo com informações do contrato (RentalInfo) se houver
   */
  findVehicleByPlate(plate: string): Observable<any> {
    const plateUpper = plate.toUpperCase().trim();
    
    // PRIMEIRO: Buscar contratos pela placa usando o endpoint de busca
    // Endpoint: https://api-loca9-erpdev.azure-api.net/erp-dev-contract/search/contracts?q=PLACA
    return this.contractsService.searchContracts({
      q: plateUpper
      // Sem filtrar por status - busca todos os contratos
    }).pipe(
      switchMap((contracts) => {
        // Filtrar contratos que realmente têm esta placa no vehicleSnapshot
        const contractsWithPlate = contracts.filter(contract => {
          const contractPlate = contract.vehicleSnapshot?.plate?.toUpperCase().trim();
          return contractPlate === plateUpper;
        });

        // Se encontrou contrato com esta placa
        if (contractsWithPlate && contractsWithPlate.length > 0) {
          // Pegar o contrato mais recente (com data de início mais recente)
          const sortedContracts = contractsWithPlate.sort((a, b) => {
            const dateA = new Date(a.startDate || 0).getTime();
            const dateB = new Date(b.startDate || 0).getTime();
            return dateB - dateA; // Mais recente primeiro
          });
          const contract = sortedContracts[0];

          // Buscar detalhes completos do contrato para obter driver e vehicleSnapshot
          return this.contractsService.getContractComplete(contract.id).pipe(
            map((contractComplete: any) => {
              // Extrair dados do veículo e contrato
              const vehicleSnapshot = contractComplete.vehicleSnapshot || contract.vehicleSnapshot;
              const driver = contractComplete.driver || contract.driver;
              
              // Montar resposta no formato esperado pelo componente
              return {
                success: true,
                data: [{
                  Id: contract.vehicleId || null,
                  Plate: plateUpper,
                  Model: vehicleSnapshot?.model || '',
                  ModelYear: vehicleSnapshot?.year || null,
                  ManufactureYear: vehicleSnapshot?.year || null,
                  CurrentMileage: vehicleSnapshot?.currentKm || 0,
                  Renavam: vehicleSnapshot?.renavam || '',
                  ChassisNumber: vehicleSnapshot?.chassis || '',
                  // Informações do contrato (RentalInfo)
                  RentalInfo: {
                    ContractId: contract.id,
                    CustomerId: contract.customerId || contractComplete.customer?.id,
                    Driver: driver ? {
                      Name: driver.name || '',
                      CpfCnpj: driver.cpfCnpj || '',
                      Phone: driver.phone || '',
                      Email: driver.email || ''
                    } : null,
                    VehicleSnapshot: vehicleSnapshot ? {
                      Model: vehicleSnapshot.model || '',
                      Year: vehicleSnapshot.year || null,
                      CurrentKm: vehicleSnapshot.currentKm || 0,
                      Renavam: vehicleSnapshot.renavam || '',
                      Chassis: vehicleSnapshot.chassis || ''
                    } : null
                  }
                }]
              };
            }),
            catchError((err) => {
              // Retornar dados básicos do contrato mesmo com erro
              const vehicleSnapshot = contract.vehicleSnapshot;
              const driver = contract.driver;
              return of({
                success: true,
                data: [{
                  Id: contract.vehicleId || null,
                  Plate: plateUpper,
                  Model: vehicleSnapshot?.model || '',
                  ModelYear: vehicleSnapshot?.year || null,
                  ManufactureYear: vehicleSnapshot?.year || null,
                  CurrentMileage: vehicleSnapshot?.currentKm || 0,
                  Renavam: vehicleSnapshot?.renavam || '',
                  ChassisNumber: vehicleSnapshot?.chassis || '',
                  RentalInfo: {
                    ContractId: contract.id,
                    CustomerId: contract.customerId,
                    Driver: driver ? {
                      Name: driver.name || '',
                      CpfCnpj: driver.cpfCnpj || '',
                      Phone: driver.phone || '',
                      Email: driver.email || ''
                    } : null,
                    VehicleSnapshot: vehicleSnapshot ? {
                      Model: vehicleSnapshot.model || '',
                      Year: vehicleSnapshot.year || null,
                      CurrentKm: vehicleSnapshot.currentKm || 0,
                      Renavam: vehicleSnapshot.renavam || '',
                      Chassis: vehicleSnapshot.chassis || ''
                    } : null
                  }
                }]
              });
            })
          );
        } else {
          // SE NÃO encontrou contrato, AÍ buscar na tabela de veículos
          return this.searchVehicleInTable(plateUpper);
        }
      }),
      catchError((err) => {
        // Se der erro na busca de contratos, tentar buscar na tabela de veículos
        return this.searchVehicleInTable(plateUpper);
      })
    );
  }

  /**
   * Busca veículo diretamente na tabela de veículos (fallback)
   * Usado quando não há contrato
   */
  private searchVehicleInTable(plate: string): Observable<any> {
    return this.vehiclesService.getByPlate(plate).pipe(
      map((vehicle) => {
        if (!vehicle) {
          return {
            success: false,
            data: [],
            message: `Nenhum veículo encontrado com a placa ${plate}`
          };
        }


        // Retornar no formato esperado, mas sem RentalInfo (sem contrato ativo)
        return {
          success: true,
          data: [{
            Id: vehicle.id,
            Plate: vehicle.plateNumber || plate,
            Model: vehicle.model || '',
            ModelYear: vehicle.modelYear || null,
            ManufactureYear: vehicle.modelYear || null,
            CurrentMileage: vehicle.currentMileage || 0,
            Renavam: vehicle.renavam || '',
            ChassisNumber: vehicle.chassisNumber || '',
            RentalInfo: null // Sem contrato ativo
          }]
        };
      }),
      catchError((err) => {
        console.error('❌ Erro ao buscar veículo na tabela:', err);
        return of({
          success: false,
          data: [],
          message: `Erro ao buscar veículo com a placa ${plate}`
        });
      })
    );
  }

  /**
   * Busca as seções de um checklist
   * @param checklistId ID do checklist
   */
  getChecklistSections(checklistId: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${checklistId}/sections`);
  }

  /**
   * Cria um item em uma seção do checklist
   * @param sectionId ID da seção
   * @param itemData Dados do item a ser criado
   */
  createSectionItem(sectionId: number, itemData: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/sections/${sectionId}/items`, itemData);
  }

  /**
   * Faz download do PDF do checklist através do backend (evita problemas de CORS)
   * O backend faz o proxy do download do Azure Blob Storage
   * @param checklistId ID do checklist
   * @param blobUrl URL do arquivo no Azure Blob Storage
   * @returns Observable com o blob do PDF
   */
  downloadPdf(checklistId: number, blobUrl: string): Observable<Blob> {
    // Codificar a URL do blob para passar como query parameter
    const encodedUrl = encodeURIComponent(blobUrl);
    const url = `${this.baseUrl}/${checklistId}/pdf/download?url=${encodedUrl}`;
    
    
    return this.http.get(url, {
      responseType: 'blob',
      observe: 'body'
    });
  }
}


